import {
  MenuButtonAddTable,
  MenuButtonBold,
  MenuButtonBulletedList,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuButtonUnderline,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
  RichTextEditor,
  RichTextEditorRef,
  TableBubbleMenu,
  TableMenuControls
} from 'mui-tiptap'
import { DependencyList, useEffect, useMemo, useRef } from 'react'

import { ALL_RICH_TEXT_FEATURES, buildRichTextExtensions, RichTextFeature } from './extensions'
import LinkMenuButton from './LinkMenuButton'
import { parseTextToHtmlParagraphs } from './richTextUtils'
import StyledRichTextEditor from './StyledRichTextEditor'

export type RichTextEditorCoreProps = {
  value?: string
  onChange?: (html: string) => void
  onBlur?: () => void
  placeholder?: string
  /** Optional features on top of the basic formatting. Defaults to none. */
  features?: RichTextFeature[]
  disabled?: boolean
  /** Pure display: no toolbar, every feature enabled so stored content renders fully. */
  readOnly?: boolean
  error?: boolean
  /** Faded look (reduced opacity) while disabled or read-only. */
  faded?: boolean
  /** Push external value changes (e.g. form reset) into the editor while it is not focused. */
  syncExternalValue?: boolean
  /** Forward the disabled flag to the underlying MUI field. */
  disableField?: boolean
  editorDependencies?: DependencyList
}

/**
 * The one Tiptap editor behind every rich text field and read-only view, so
 * features and the link policy cannot drift between copies again.
 */
export default function RichTextEditorCore({
  value,
  onChange,
  onBlur,
  placeholder,
  features = [],
  disabled = false,
  readOnly = false,
  error = false,
  faded = false,
  syncExternalValue = false,
  disableField = false,
  editorDependencies
}: RichTextEditorCoreProps) {
  const editorRef = useRef<RichTextEditorRef>(null)
  const editable = !disabled && !readOnly
  const activeFeatures = readOnly ? ALL_RICH_TEXT_FEATURES : features
  const hasTable = activeFeatures.includes('table')
  const hasLink = activeFeatures.includes('link')

  const extensions = useMemo(
    () => buildRichTextExtensions({ features: activeFeatures, placeholder, editable }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeFeatures.join(','), placeholder, editable]
  )

  useEffect(() => {
    if (!syncExternalValue) return
    const editor = editorRef.current?.editor
    if (!editor || editor.isDestroyed) return
    if (!editor.isFocused || !editor.isEditable) {
      // Use queueMicrotask per https://github.com/ueberdosis/tiptap/issues/3764#issuecomment-1546854730
      queueMicrotask(() => {
        const currentSelection = editor.state.selection
        editor
          .chain()
          .setContent(value || '')
          .setTextSelection(currentSelection)
          .run()
      })
    }
  }, [value, syncExternalValue])

  const inactive = disabled || readOnly
  const stateClasses = [error && 'error', inactive && 'disabled', inactive && faded && 'faded']
    .filter(Boolean)
    .join(' ')

  return (
    <StyledRichTextEditor>
      <RichTextEditor
        ref={editorRef}
        content={parseTextToHtmlParagraphs(value)}
        onUpdate={({ editor }) => onChange?.(editor.getHTML())}
        onBlur={onBlur}
        editable={editable}
        extensions={extensions}
        renderControls={() =>
          editable ? (
            <MenuControlsContainer>
              <MenuSelectHeading />
              <MenuDivider />
              <MenuButtonBold />
              <MenuButtonItalic />
              <MenuButtonUnderline />
              <MenuDivider />
              <MenuButtonOrderedList />
              <MenuButtonBulletedList />
              {hasTable && (
                <>
                  <MenuDivider />
                  <MenuButtonAddTable />
                  <TableMenuControls />
                </>
              )}
              {hasLink && (
                <>
                  <MenuDivider />
                  <LinkMenuButton />
                </>
              )}
            </MenuControlsContainer>
          ) : null
        }
        RichTextFieldProps={{
          className: `rich-text-field ${stateClasses}`,
          variant: 'standard',
          ...(disableField && { disabled: inactive })
        }}
        className={`rich-text-editor ${error ? 'has-error' : ''} ${inactive ? 'disabled' : ''}`}
        editorDependencies={editorDependencies}
      >
        {() => (editable && hasTable ? <TableBubbleMenu /> : null)}
      </RichTextEditor>
    </StyledRichTextEditor>
  )
}
